const TRANSPORT_ERROR_PATTERNS = [
    'networkerror',
    'network error',
    'load failed',
    'failed to fetch',
    'fetch failed',
    'the network connection was lost',
    'net::err',
    'econnrefused',
    'ecanceled',
    'etimedout',
    'timeout',
    'enotfound',
    'dns',
    'offline'
];

export const SHOPWARE_CIRCUIT_OPEN_ERROR = 'ShopwareCircuitOpenError';
export const SHOPWARE_TIMEOUT_REASON = 'shopware-timeout';

/** @internal exported for testing only */
export function containsTransportSignal(msg: string): boolean {
    return TRANSPORT_ERROR_PATTERNS.some((pattern) => msg.includes(pattern));
}

type ErrorLike = {
    statusCode?: number;
    status?: number;
    statusMessage?: string;
    message?: string;
    name?: string;
    response?: { status?: number };
    cause?: { status?: number; message?: string };
    data?: { status?: number };
};

/** @internal exported for testing only */
export function extractStatus(error: unknown): number | null {
    const e = error as ErrorLike;
    const status = e?.statusCode ?? e?.status ?? e?.response?.status ?? e?.cause?.status ?? e?.data?.status;

    return typeof status === 'number' ? status : null;
}

/** @internal exported for testing only */
export function normalizeMessage(error: unknown): string {
    const e = error as ErrorLike;
    return String(e?.message ?? e?.cause?.message ?? e?.statusMessage ?? error ?? '').toLowerCase();
}

/** @internal exported for testing only */
export function isAbortError(error: unknown): boolean {
    return (error as ErrorLike)?.name === 'AbortError';
}

/** @internal exported for testing only */
export function isDowntimeError(error: unknown): boolean {
    if ((error as ErrorLike)?.name === SHOPWARE_CIRCUIT_OPEN_ERROR) {
        return true;
    }

    const status = extractStatus(error);

    if (status !== null) {
        return status >= 500;
    }
    const msg = normalizeMessage(error);

    const hasTransportSignal = containsTransportSignal(msg);

    const looksLikeHttp4xx = /\b(401|403|404|412)\b/.test(msg);

    if (looksLikeHttp4xx) return false;

    return hasTransportSignal;
}

export function useShopwareDowntime() {
    const route = useRoute();
    const router = useRouter();
    const shopwareDownToast = useState<HTMLDivElement | null>('shopware-down-toast-el', () => null);

    const down = useState<boolean>('shopware-down', () => false);

    const clearDowntimeQueryFlag = () => {
        if (!import.meta.client) return;
        if (!route.query.shopwareDown) return;

        const queryWithoutFlag = { ...route.query };
        delete queryWithoutFlag.shopwareDown;

        void router.replace({
            path: route.path,
            query: queryWithoutFlag,
            hash: route.hash
        });
    };

    const closeDowntimeToast = () => {
        if (!import.meta.client) return;

        const toastApi = useToast();

        if (shopwareDownToast.value) {
            try {
                toastApi.hide({}, shopwareDownToast.value, 'button');
            } catch (_error) {
                // ignore and continue with fallback
            }
            shopwareDownToast.value = null;
            return;
        }

        const fallbackToast = document.querySelector<HTMLDivElement>('.shopware-down-toast');
        if (fallbackToast) {
            try {
                toastApi.hide({}, fallbackToast, 'button');
            } catch (_error) {
                fallbackToast.remove();
            }
        }
    };

    const markDown = () => {
        down.value = true;
    };
    const markUp = () => {
        down.value = false;
        closeDowntimeToast();
        clearDowntimeQueryFlag();
    };

    const capture = (error: unknown) => {
        if (isDowntimeError(error)) markDown();
    };

    return {
        shopwareDown: computed(() => down.value),
        markDown,
        markUp,
        capture
    };
}
